// pages/LegalPage.jsx — Anamoria SPA
// Route: /privacy (public).  /terms reserved (held until counsel completes the Terms).
//
// v1.3 (2026-09-26) — TM notice: ® → ™ and dropped "registered". The mark has a
//   USPTO Notice of Allowance but is NOT yet registered; counsel confirmed ™ is
//   correct while the application is pending (® only after registration issues).
//   Footer now reads "Anamoria™ is a trademark of Anamoria Inc." Copy-only; no logic change.
//
// v1.2 — NS-61/O-b: added the two contributor-consent notices as bundled, versioned,
//   self-proving docs (same pattern as privacy): 'contributor-consent-group' and
//   'contributor-consent-member'. Each is byte-identical to its src/legal/*.md file
//   whose sha256 is recorded in policy_documents, so a contributor consent row points
//   at a retrievable, integrity-checked copy of exactly the wording shown. Routes
//   /contributor-consent-group and /contributor-consent-member (public, record pages —
//   NOT inserted into the claim flow). No renderer change.
//
// v1.1 — June 14, 2026
//   - Added a trademark/copyright footer note below the document
//     ("© 2026 Anamoria Inc · Anamoria® is a registered trademark."), per the
//     site-wide notice decision (Session Log §4; ® on the word mark only).
//     Adds .footerNote to the CSS module (v1.1). No other change.
//
// v1.0 — June 14, 2026
//   - New public page that renders a bundled legal document.
//   - Content is the published Privacy Policy, imported as a RAW STRING via
//     Vite's built-in `?raw` (no new dependency). Because it is bundled, the
//     page renders offline (SPA fallback per Build Plan v1.3 §7), and because
//     it is the SAME file whose sha256 is recorded in policy_documents, each
//     consent row is self-proving (Build Plan v1.3 §6, §8).
//   - No markdown/runtime dependency: a small deterministic renderer handles
//     only the constructs this document uses — `#`/`##` headings, paragraphs
//     (with soft line breaks preserved), `- ` bullet lists, the `---` rule,
//     and autolinked https URLs (the named sub-processors).
//   - `doc` prop selects which document; only 'privacy' is wired now. Add
//     'terms' to DOCS once the Terms is finalized, then expose the /terms route.
//
// Source of truth: src/legal/privacy-policy-1.0.md — committed BYTE-IDENTICAL
//   to Anamoria_Privacy_Policy_Published_v1_0.md. When the policy version
//   changes, update the file AND the policy_documents hash together.

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import privacyMarkdown from '../legal/privacy-policy-1.0.md?raw';
import contributorConsentGroupMarkdown from '../legal/contributor-consent-group-1.0.md?raw';
import contributorConsentMemberMarkdown from '../legal/contributor-consent-member-1.0.md?raw';
import styles from './LegalPage.module.css';

// Bundled documents. Add `terms` here once the Terms is finalized.
// The contributor-consent docs (NS-61/O-b) are versioned + self-proving: each file's
// sha256 is the content_hash recorded in policy_documents for its doc_type.
const DOCS = {
  privacy: privacyMarkdown,
  'contributor-consent-group': contributorConsentGroupMarkdown,
  'contributor-consent-member': contributorConsentMemberMarkdown,
};

// ─── Minimal, deterministic block parser (no markdown library) ───
function parseBlocks(src) {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  let list = [];
  const flushPara = () => {
    if (para.length) { blocks.push({ type: 'p', lines: para.slice() }); para = []; }
  };
  const flushList = () => {
    if (list.length) { blocks.push({ type: 'ul', items: list.slice() }); list = []; }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.trim() === '') { flushPara(); flushList(); continue; }
    if (line === '---') { flushPara(); flushList(); blocks.push({ type: 'hr' }); continue; }
    if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h1', text: line.slice(2) }); continue; }
    if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(3) }); continue; }
    if (line.startsWith('- ')) { flushPara(); list.push(line.slice(2)); continue; }
    flushList(); para.push(line);
  }
  flushPara(); flushList();
  return blocks;
}

// Split a text run into plain text + safe https links. React escapes the text
// nodes, so this is XSS-safe; the href guard rejects anything but http(s).
const URL_RE = /(https?:\/\/[^\s)]+)/g;
function renderInline(text, keyBase) {
  return text.split(URL_RE).map((part, i) => {
    if (i % 2 === 1 && /^https?:\/\//.test(part)) {
      return (
        <a key={`${keyBase}-a${i}`} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return part;
  });
}

function renderParagraph(linesArr, key) {
  const out = [];
  linesArr.forEach((ln, i) => {
    if (i > 0) out.push(<br key={`${key}-br${i}`} />);
    out.push(...renderInline(ln, `${key}-l${i}`));
  });
  return <p key={key}>{out}</p>;
}

export default function LegalPage({ doc = 'privacy' }) {
  const navigate = useNavigate();
  const source = DOCS[doc];
  const blocks = useMemo(() => (source ? parseBlocks(source) : []), [source]);

  return (
    <div className={styles.page}>
      <div className={styles.content}>

        {/* Top bar: brand + back */}
        <div className={styles.topbar}>
          <Link to="/" className={styles.brand}>Anamoria</Link>
          <button type="button" className={styles.back} onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        {!source ? (
          <p className={styles.unavailable}>This document is not available yet.</p>
        ) : (
          <article className={styles.doc}>
            {blocks.map((b, i) => {
              const key = `b${i}`;
              if (b.type === 'h1') return <h1 key={key}>{b.text}</h1>;
              if (b.type === 'h2') return <h2 key={key}>{renderInline(b.text, key)}</h2>;
              if (b.type === 'hr') return <hr key={key} />;
              if (b.type === 'ul') {
                return (
                  <ul key={key}>
                    {b.items.map((it, j) => (
                      <li key={`${key}-i${j}`}>{renderInline(it, `${key}-i${j}`)}</li>
                    ))}
                  </ul>
                );
              }
              return renderParagraph(b.lines, key);
            })}
          </article>
        )}

        <p className={styles.footerNote}>
          © 2026 Anamoria Inc · Anamoria™ is a trademark of Anamoria Inc.
        </p>

      </div>
    </div>
  );
}
